import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik, FormikProvider } from 'formik'
import * as yup from 'yup'

import {
  postCreatePayrollCompany,
  FETCH_PAYROLL_PROFILE_KEY,
  fetchPayrollProfileIfNeeded,
  POST_CREATE_PAYROLL_COMPANY_KEY,
} from '../payrollActions'
import { selectPayrollProfile } from '../payroll.selectors'
import { getCurrentUser } from '@/selectors/user.selectors'
import {
  getIsFetching,
  getIsFetchingOrNotStarted,
} from '../../../reducers/fetch'
import { PayrollSetupList } from './PayrollSetupList'
import { PrefilledNoticeContainer } from './PrefilledNoticeContainer'
import {
  GEP_ENROLL_PATHS,
  ONBOARDING_STEPS,
  useUpdatePayrollOnboardingStep,
} from '../helpers'
import { PayrollError } from '../PayrollError'
import {
  Button,
  Card,
  Link,
  Text,
  Form,
  Separator,
  CardColors,
  Loader,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
// TODO: Migrate to V2
import {
  makeReqStringSchema,
  makeReqPhoneNumberSchema,
  makeReqEmailSchema,
  parseNationalPhoneNumber,
} from '@/base'

const validationSchema = yup.object({
  first_name: makeReqStringSchema({ field: 'first name' }),
  last_name: makeReqStringSchema({ field: 'last name' }),
  phone: makeReqPhoneNumberSchema(),
  email: makeReqEmailSchema({ field: 'email' }),
  company_name: makeReqStringSchema({ field: 'business name' }),
})

const CompanyForm = ({ goToNextStep }: { goToNextStep: () => void }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const user = useReselector(getCurrentUser)
  const isPosting = useReselector(
    getIsFetching,
    POST_CREATE_PAYROLL_COMPANY_KEY
  )
  const track = useAnalyticsTrack()
  useUpdatePayrollOnboardingStep(ONBOARDING_STEPS.companyCreate)

  const formik = useFormik({
    initialValues: {
      first_name: user?.firstName || '',
      last_name: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phoneNumber || '',
      company_name: user?.financialProfile?.businessName || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const res = await postCreatePayrollCompany({
        user: {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: parseNationalPhoneNumber(values.phone) || null,
        },
        company: { name: values.company_name },
      })(dispatch)

      if (res) {
        track('completed payroll step - company creation')
        goToNextStep()
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-6">
        <Text variant={TypographyStyles.H2}>Company Setup</Text>

        <Text variant={TypographyStyles.BODY_MD}>
          Please confirm the following details.
        </Text>

        <Separator />

        <div className="flex flex-wrap gap-6">
          <div className="w-full md:w-1/2">
            <Form.Input
              name="first_name"
              label="First name"
              description="Your legal name."
              required
            />
          </div>
          <div className="w-full md:w-1/2">
            <Form.Input
              name="last_name"
              label="Last Name"
              description="Your legal name."
              required
            />
          </div>
        </div>

        <div className="w-full md:w-3/4">
          <Form.Input name="email" label="Email Address" required />
        </div>

        <div className="w-full md:w-3/4">
          <Form.Input
            name="phone"
            label="Phone"
            required
            componentType="phone"
          />
        </div>

        <div className="w-full">
          <Form.Input
            name="company_name"
            label="Company Name"
            description={
              <>
                Make sure this is your legal name, not your DBA.{' '}
                <Link
                  href="https://support.joinheard.com/hc/en-us/articles/7257775812887"
                  newPage
                  className="inline underline font-bold"
                >
                  Where can I find this?
                </Link>
              </>
            }
            required
          />
        </div>

        <div className="flex flex-wrap gap-4 mt-6">
          <div className="w-full md:w-1/2">
            <Button
              variant="secondary"
              onClick={() => navigate(GEP_ENROLL_PATHS.overview)}
            >
              Cancel Enrollment
            </Button>
          </div>
          <div className="w-full md:w-1/2">
            <Button onClick={formik.submitForm} loading={isPosting}>
              Save & Continue
            </Button>
          </div>
        </div>
      </div>
    </FormikProvider>
  )
}

export const PayrollCompanySetup = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const payrollProfile = useReselector(selectPayrollProfile)
  const isLoading = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_PAYROLL_PROFILE_KEY
  )

  const goToNextStep = useCallback(() => {
    navigate(GEP_ENROLL_PATHS.federal)
  }, [navigate])

  useEffect(() => {
    dispatch(fetchPayrollProfileIfNeeded())
  }, [dispatch])

  // This step cannot be redone or edited so make sure this form is only submitted once
  return (
    <div>
      <Card>
        <Text variant={TypographyStyles.DISPLAY_XS}>Setting Up Payroll</Text>

        <Separator />

        <PrefilledNoticeContainer />

        <Loader active={isLoading} />

        <PayrollError fetchKey={POST_CREATE_PAYROLL_COMPANY_KEY} />

        {!isLoading && (
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="w-full lg:w-2/3 flex flex-col gap-6">
              {payrollProfile?.gustoCompanyUuid ? (
                <>
                  <Text variant={TypographyStyles.H3}>Company Created</Text>
                  <Card color={CardColors.LEVEL_1}>
                    <Text variant={TypographyStyles.BODY_LG}>
                      Company details successfully added. You may continue.
                    </Text>
                  </Card>
                  <div className="mt-10">
                    <Button onClick={goToNextStep}>Continue</Button>
                  </div>
                </>
              ) : (
                <CompanyForm goToNextStep={goToNextStep} />
              )}
            </div>

            <div className="w-full lg:w-1/3">
              <PayrollSetupList />
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
